import React, {
  MouseEvent,
  ReactNode,
  useState,
} from 'react';
import clsx from 'clsx';
import compact from 'lodash/compact';
import IconButton from '@mui/material/IconButton';
import Menu from '@mui/material/Menu';
import MenuItem from '@mui/material/MenuItem';
import Skeleton from 'core/components/Skeleton';
import ToolTip from 'core/components/Tooltip';
import DownloadIcon from 'core/icons/download';
import Spinner from 'pages/Dashboard/components/Spinner';
import type {
  ReportState,
  RunOutputFile,
  RunOutputSource,
} from 'views/EMR/components/Panel/RunOutputPanel/types';
import {
  formatRunSummaryLine,
} from 'views/EMR/components/Panel/RunOutputPanel/utils';
import {
  useDownloadAllRunOutputFiles,
  useDownloadRunOutputFile,
  useRunOutput,
} from 'views/EMR/components/Panel/RunOutputPanel/hooks';
import {
  getFileIcon,
} from 'pages/Dashboard/pages/Charts/pages/Details/components/Multimedia/columns';
import {
  DEFAULT_CONNECTOR,
} from 'core/constants';

// The table row opens the panel on click, so nothing in the actions cell may reach it.
const stopPropagation = (e: MouseEvent<HTMLElement>) => {
  e.stopPropagation();
};

type RunSummaryLineProps = {
  state: ReportState | null | undefined;
  fileCount: number;
  exceptionCount: number;
  // Shown before the counts, e.g. the run's parameters; still shown while the run isn't ready.
  prefix?: string | null;
};

export function RunSummaryLine({
  state,
  fileCount,
  exceptionCount,
  prefix,
}: RunSummaryLineProps) {
  const text = compact([
    prefix,
    state === 'Ready' ? formatRunSummaryLine(fileCount, exceptionCount) : null,
  ]).join(DEFAULT_CONNECTOR);

  return text ? (
    <span className="block mt-0.5 text-xs font-normal text-gray-600">
      {text}
    </span>
  ) : null;
}

const iconButtonClassName = '!w-7 !h-7 hover:!bg-gray-100';
const disabledClassName = '!opacity-30 !cursor-not-allowed !pointer-events-auto';
const iconClassName = '!w-4 !h-4 text-gray-700';

type ActionButtonProps = {
  label: string;
  tooltip: ReactNode;
  disabled: boolean;
  onClick: (e: MouseEvent<HTMLButtonElement>) => void;
  children: ReactNode;
  buttonProps?: Record<string, string | undefined>;
};

function ActionButton({
  label,
  tooltip,
  disabled,
  onClick,
  children,
  buttonProps,
}: ActionButtonProps) {
  return (
    <ToolTip title={tooltip}>
      {/* The span keeps the tooltip working on a disabled button and catches its clicks. */}
      <span
        role="presentation"
        onClick={stopPropagation}
      >
        <IconButton
          aria-label={label}
          disabled={disabled}
          onClick={(e) => {
            e.stopPropagation();
            onClick(e);
          }}
          className={clsx(iconButtonClassName, 'relative', { [disabledClassName]: disabled })}
          {...buttonProps}
        >
          {children}
        </IconButton>
      </span>
    </ToolTip>
  );
}

const menuItemClassName = 'flex items-center gap-2 !px-2.5 !py-2 !min-h-0 rounded-md !text-xs hover:!bg-gray-100';

type RunFileMenuProps = {
  source: RunOutputSource;
  anchorEl: HTMLElement | null;
  onClose: () => void;
  onDownload: (reportOutputFileId: number | null) => void;
  onDownloadAll: (files: RunOutputFile[]) => void;
};

type RunFileMenuErrorProps = {
  message?: string;
  onRetry: () => void;
};

function RunFileMenuError({ message, onRetry }: RunFileMenuErrorProps) {
  return (
    <div className="flex flex-col items-start">
      <p className="px-2.5 py-2 text-xs text-red-700">
        {message || 'Could not load the files.'}
      </p>
      <button
        type="button"
        className="mx-2.5 mb-2 text-xs font-semibold text-blue-600 hover:underline"
        onClick={(e) => {
          e.stopPropagation();
          onRetry();
        }}
      >
        Try again
      </button>
    </div>
  );
}

function RunFileMenu({
  source,
  anchorEl,
  onClose,
  onDownload,
  onDownloadAll,
}: RunFileMenuProps) {
  const open = Boolean(anchorEl);
  const {
    data,
    error,
    isError,
    isPending,
    refetch,
  } = useRunOutput(source, { enabled: open });

  const files = data?.files ?? [];
  const isLoaded = !isError && !isPending;

  return (
    <Menu
      anchorEl={anchorEl}
      open={open}
      onClose={onClose}
      // Menu renders in a portal, but React events still bubble to the row through it.
      onClick={stopPropagation}
      anchorOrigin={{ vertical: 'bottom', horizontal: 'right' }}
      transformOrigin={{ vertical: 'top', horizontal: 'right' }}
      slotProps={{
        paper: {
          className: 'w-[340px] !rounded-lg border border-gray-200 !shadow-lg p-1.5',
        },
        list: {
          className: '!p-0',
        },
      }}
    >
      {isError && (
        <RunFileMenuError
          message={error?.message}
          onRetry={refetch}
        />
      )}
      {!isError && isPending && (
        <div
          className="flex flex-col gap-4 py-2 px-4"
          aria-label="Loading files"
        >
          <Skeleton variant="rounded" className="!h-4 w-full" />
          <Skeleton variant="rounded" className="!h-4 w-full" />
          <Skeleton variant="rounded" className="!h-4 w-full" />
        </div>
      )}
      {isLoaded && files.map((file, index) => (
        <MenuItem
          // A null id means "the run's first file", so the index keeps keys unique.
          key={file.reportOutputFileId ?? `index-${index}`}
          className={menuItemClassName}
          onClick={(e) => {
            e.stopPropagation();
            onDownload(file.reportOutputFileId);
            onClose();
          }}
        >
          {getFileIcon(file.fileName)}
          <span className="truncate">{file.fileName}</span>
        </MenuItem>
      ))}
      {isLoaded && (
        <div
          aria-hidden
          className="my-1 h-px bg-gray-200"
        />
      )}
      {isLoaded && (
        <MenuItem
          className={menuItemClassName}
          onClick={(e) => {
            e.stopPropagation();
            onDownloadAll(files);
            onClose();
          }}
        >
          <span className="font-semibold text-blue-500">Download all</span>
        </MenuItem>
      )}
    </Menu>
  );
}

type RunDownloadButtonProps = {
  source: RunOutputSource;
  state: ReportState | null | undefined;
  fileCount: number;
  isFileMissing?: boolean | null;
  fileMissingReason?: string | null;
};

function RunDownloadButton({
  source,
  state,
  fileCount,
  isFileMissing,
  fileMissingReason,
}: RunDownloadButtonProps) {
  const [anchorEl, setAnchorEl] = useState<HTMLElement | null>(null);
  const { mutate, pendingFileId } = useDownloadRunOutputFile(source);
  const { downloadAll, isPending: isDownloadAllPending } = useDownloadAllRunOutputFiles(source);

  const isMulti = fileCount > 1;
  const disabled = state !== 'Ready' || fileCount <= 0 || Boolean(isFileMissing);
  const isDownloading = pendingFileId !== undefined || isDownloadAllPending;

  let tooltip: ReactNode = isMulti ? 'Choose a file' : 'Download';
  if (isFileMissing) {
    tooltip = fileMissingReason;
  }

  const menuOpen = Boolean(anchorEl);

  return (
    <>
      <ActionButton
        label="Download"
        tooltip={tooltip}
        disabled={disabled}
        onClick={(e) => {
          if (isMulti) {
            setAnchorEl(e.currentTarget);
          } else {
            mutate({});
          }
        }}
        buttonProps={isMulti
          ? {
            'aria-haspopup': 'menu',
            'aria-expanded': menuOpen ? 'true' : undefined,
          }
          : undefined}
      >
        {isDownloading
          ? <Spinner size={16} />
          : <DownloadIcon className={iconClassName} />}
        {isMulti && !disabled && (
          <span className="absolute top-1 right-1 translate-x-[45%] -translate-y-[40%] h-3.5 min-w-3.5 px-1 rounded-full bg-gray-400 text-white text-[10px] leading-none font-semibold tabular-nums [text-box:trim-both_cap_alphabetic] grid place-items-center">
            {fileCount}
          </span>
        )}
      </ActionButton>
      {isMulti && (
        <RunFileMenu
          source={source}
          anchorEl={anchorEl}
          onClose={() => setAnchorEl(null)}
          onDownload={(reportOutputFileId) => mutate({ reportOutputFileId })}
          onDownloadAll={(files) => {
            downloadAll(files);
          }}
        />
      )}
    </>
  );
}

type RunActionsCellProps = {
  source: RunOutputSource;
  state: ReportState | null | undefined;
  fileCount: number;
  isFileMissing?: boolean | null;
  fileMissingReason?: string | null;
  extraActions?: ReactNode;
};

export function RunActionsCell({
  source,
  state,
  fileCount,
  isFileMissing,
  fileMissingReason,
  extraActions,
}: RunActionsCellProps) {
  return (
    <div className="flex justify-end gap-0.5">
      <RunDownloadButton
        source={source}
        state={state}
        fileCount={fileCount}
        isFileMissing={isFileMissing}
        fileMissingReason={fileMissingReason}
      />
      {extraActions}
    </div>
  );
}
